import Button from 'shared/Buttons/Button/Button'
import { useEffect, useState } from 'react'
import { useCheckpointCriteriaState } from 'screens/Admin/ProjectCheckpoints/hooks/useCheckpointCriteriaState'
import { IProjectCheckpointCriterion } from 'screens/Admin/ProjectCheckpoints/projectCheckpoints.types'
import { tinyUuid } from 'utils/tiny-uuid'
import CriteriaSheet from 'screens/Admin/ProjectCheckpoints/components/CriteriaSheet'
import projectCheckpointsApi from 'screens/Admin/ProjectCheckpoints/projectCheckpoints.api'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import { useSetRecoilState } from 'recoil'
import checkpointsCriteriaAtom from 'screens/Admin/ProjectCheckpoints/checkpointsCriteria.atom'
import eventBus from 'services/eventBus'
import ConfirmDeleteModal from 'shared/Modal/ConfirmDeleteModal'

interface CheckpointCriteriaProps {
  checkpointId: string
}

const CheckpointCriteria = (props: CheckpointCriteriaProps) => {
  const { criteriaState, criteriaMap } = useCheckpointCriteriaState(props.checkpointId)
  const [criteriaDraft, setCriteriaDraft] = useState(criteriaState)
  const [criterionToDelete, setCriterionToDelete] = useState<string | null>()

  const { wait } = useHttpLoader()

  const setCriteriaState = useSetRecoilState(checkpointsCriteriaAtom)

  useEffect(() => {
    setCriteriaDraft(criteriaState)
  }, [criteriaState])

  const isValid = (criterion: IProjectCheckpointCriterion) =>
    criterion && !!criterion.title && !!criterion.maxBalls && criterion.minBalls >= 0

  const handleSave = (id: string) => () => {
    const criterionDraft = criteriaDraft.find((c) => c.id === id)
    if(!isValid(criterionDraft)) {
      eventBus.pub('ntf.message', { message: 'Заполните обязательные поля', type: 'error' })
      return
    }
    const criterion = criteriaMap[id]
    const promise = criterion
      ? projectCheckpointsApi.updateCriterion(props.checkpointId, id, criterionDraft)
      : projectCheckpointsApi.createCriterion(props.checkpointId, criterionDraft)

    wait(promise, (resp) => {
      if (resp.status === 'success') {
        setCriteriaState((prev) => ({
          ...prev,
          items: {
            ...prev.items,
            [props.checkpointId]: [resp.body, ...prev.items[props.checkpointId].filter((c) => c.id !== id)],
          },
        }))
        eventBus.pub('ntf.message', { message: 'Критерий сохранен', type: 'info' })
      }
    })
  }

  const handleAdd = () => {
    const defaultCriterion: IProjectCheckpointCriterion = {
      id: tinyUuid(),
      title: '',
      description: '',
      maxBalls: 100,
      minBalls: 0,
    }
    setCriteriaDraft(prev => [defaultCriterion, ...prev])
  }

  const hasUnsavedChanges = (criteriaDraft: IProjectCheckpointCriterion) => {
    const criteria = criteriaMap[criteriaDraft.id]
    if (!criteria) return true
    let hasUnsavedChanges = false
    for (const key in criteriaDraft) {
      if (criteriaDraft[key] !== criteria[key]) hasUnsavedChanges = true
    }
    return hasUnsavedChanges
  }

  const handleChange = (id: string) => (val: any, name: string) => {
    setCriteriaDraft((prev) => {
      const newCriteria = prev.map((c) => (c.id === id ? { ...c, [name]: val } : c))
      return newCriteria
    })
  }
  const handleDelete = () => {
    const criterion = criteriaMap[criterionToDelete!]
    if (criterion) {
      wait(projectCheckpointsApi.deleteCriterion(props.checkpointId, criterionToDelete), (resp) => {
        if (resp.status === 'success') {
          setCriteriaState((prev) => ({
            ...prev,
            items: {
              ...prev.items,
              [props.checkpointId]: prev.items[props.checkpointId].filter((c) => c.id !== criterionToDelete),
            },
          }))
          eventBus.pub('ntf.message', { message: 'Критерий удален', type: 'info' })
        }
      })
    }
    setCriteriaDraft((prev) => prev.filter((c) => c.id !== criterionToDelete))
    setCriterionToDelete(null)
  }

  const isDisabledAddNewCriteria = () => criteriaDraft.some((c) => hasUnsavedChanges(c))

  return (
    <>
      {criterionToDelete && (
        <ConfirmDeleteModal onDelete={handleDelete} onClose={() => setCriterionToDelete(null)} entityName='Критерий' />
      )}
      <div className='flex-lines'>
        <Button theme='grey-outline' style={{ width: 'fit-content' }} onClick={handleAdd} disabled={isDisabledAddNewCriteria()}>
          Добавить критерий
        </Button>
        <div className='grid-form-2'>
          {criteriaDraft.map((criteria) => (
            <CriteriaSheet criteria={criteria} onChange={handleChange(criteria.id)} key={criteria.id}>
              <div className='form-footer'>
                <div className='inline-flex-gap'>
                  <Button theme='primary' disabled={!hasUnsavedChanges(criteria)} onClick={handleSave(criteria.id)}>
                    Сохранить
                  </Button>
                  <Button theme='danger' onClick={() => setCriterionToDelete(criteria.id)}>
                    Удалить
                  </Button>
                </div>
              </div>
            </CriteriaSheet>
          ))}
        </div>
      </div>
    </>
  )
}

export default CheckpointCriteria
